import React, { useState, useEffect } from 'react';
import { db } from '../FireBase'; // Import Firebase setup
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Button, Form, Table } from 'react-bootstrap';
import bcrypt from 'bcryptjs';
import ToastAlert from '../components/ToastAlert';

const UserSettings = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    Password: '',
    role: 'Admin' // Default role
  });
  const [users, setUsers] = useState([]);
  const [editUserId, setEditUserId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let hashedPassword = formData.Password;

      // Hash the password only if it's a new password entered
      if (formData.Password.trim() !== '') {
        hashedPassword = await bcrypt.hash(formData.Password, 10); // 10 is the salt rounds
      } else if (editUserId) {
        // If editing and no new password is entered, retain the existing password
        const userDoc = users.find((user) => user.id === editUserId);
        hashedPassword = userDoc.Password;
      }

      // Prepare data to store in Firestore
      const dataToStore = {
        ...formData,
        Password: hashedPassword, // Use the hashed or existing password
      };

      if (editUserId) {
        // Update existing user
        const userDoc = doc(db, 'crm_users', editUserId);
        await updateDoc(userDoc, dataToStore);
        setToastMessage('User updated successfully!');
        setToastVariant('success');
        setShowToast(true);
      } else {
        // Add new user
        await addDoc(collection(db, 'crm_users'), dataToStore);
        setToastMessage('User added successfully!');
        setToastVariant('success');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setToastMessage('Something went wrong!!!');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setEditUserId(null);
      setFormData({ username: '', email: '', Password: '', role: 'Admin' });
      fetchUsers(); // Refresh user list
    }
  };

  // Fetch users from Firestore
  const fetchUsers = async () => {
    const userSnapshot = await getDocs(collection(db, 'crm_users'));
    const userList = userSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
    setUsers(userList);
  };

  // Delete user
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      await deleteDoc(doc(db, 'crm_users', id));
      setToastMessage('User deleted successfully!');
      setToastVariant('danger');
      setShowToast(true);
      fetchUsers(); // Refresh user list
    }
  };

  // Edit user
  const handleEdit = (user) => {
    setFormData({
      ...user,
      Password: '', // Set the password field blank during edit
    });
    setEditUserId(user.id);
  };

  useEffect(() => {
    fetchUsers(); // Fetch users on component mount
  }, []);

  return (
    <div className="user-settings" style={{ padding: '20px' }}>
      <ToastAlert
        show={showToast}
        setShow={setShowToast}
        message={toastMessage}
        variant={toastVariant}
      />

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Username</Form.Label>
          <Form.Control
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Password</Form.Label>
          <Form.Control
            type="password"
            name="Password"
            value={formData.Password}
            onChange={handleInputChange}
            required={!editUserId} // Password is required only for new users
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Role</Form.Label>
          <Form.Control
            as="select"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            required
          >
            <option value="Admin">Admin</option>
            <option value="Billing">Billing</option>
            <option value="Logistics">Logistics</option>
          </Form.Control>
        </Form.Group>

        <Button size="sm" variant="success" type="submit">
          {editUserId ? 'Update User' : 'Add User'}
        </Button>
      </Form>

      <h3 className="mt-4">User List</h3>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>
                <Button size="sm" variant="outline-warning" onClick={() => handleEdit(user)}>
                  Edit
                </Button>{' '}
                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(user.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default UserSettings;